import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'sidebar-collapsed';
const MOBILE_BREAKPOINT = 1024;

function getInitialCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // New users (no stored preference) start with the nav open.
    if (stored === null) return false;
    return stored === 'true';
  } catch {
    return false;
  }
}

/**
 * A new sign-up starts with the nav open. The saved fold belongs to the
 * browser, not the user (another account's, or the Clu dock's), so finishing
 * onboarding forgets it and the app opens on the default.
 */
export function openSidebarAfterSignUp() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    document.documentElement.dataset.sidebarCollapsed = 'false';
  } catch {
    // Storage or document unavailable: nothing was saved either.
  }
}

// Eagerly set the data attribute so the CSS-driven margin is correct before
// React renders. Prevents a layout flash on first paint.
try {
  document.documentElement.dataset.sidebarCollapsed = String(
    getInitialCollapsed()
  );
} catch {
  // SSR / test env — no document
}

export function useSidebarState() {
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsed);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Persist collapse state + push CSS variable so layout shifts are pure-CSS
  // (no React re-render of the entire page tree).
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isCollapsed));
    } catch {
      // ignore
    }
    document.documentElement.dataset.sidebarCollapsed = String(isCollapsed);
  }, [isCollapsed]);

  // Auto-close mobile drawer on resize above breakpoint
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= MOBILE_BREAKPOINT) {
        setIsMobileOpen((prev) => (prev ? false : prev));
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const expand = useCallback(() => setIsCollapsed(false), []);
  const collapse = useCallback(() => setIsCollapsed(true), []);
  const toggle = useCallback(() => setIsCollapsed((prev) => !prev), []);
  const openMobile = useCallback(() => setIsMobileOpen(true), []);
  const closeMobile = useCallback(() => setIsMobileOpen(false), []);

  return useMemo(
    () => ({
      isCollapsed,
      isMobileOpen,
      toggle,
      expand,
      collapse,
      openMobile,
      closeMobile,
    }),
    [
      isCollapsed,
      isMobileOpen,
      toggle,
      expand,
      collapse,
      openMobile,
      closeMobile,
    ]
  );
}
